import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogComponent, AutocompleteComponent, Inputs } from '../../../../../../Components';
import debounce from 'lodash/debounce';
import { GetOpenTenantLeads, CreateActivity } from '../../../../../../Services';
import { showError, showSuccess } from '../../../../../../Helper';
import { config } from '../../../../../../config';

const RequestOfferLetterDialog = ({
  isOpen,
  onClose,
  unitData,
  currentUser,
}) => {
  const { t } = useTranslation('UnitsView');
  const translationPath = '';
  const parentTranslationPath = 'UnitsView';
  
 
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [leadData, setLeadData] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [remarks, setRemarks] = useState(''); 
  const [searchInput, setSearchInput] = useState('');
  const [activityName, setActivityName] = useState('Request for Sending Offer Letter'); 

  const createdBy = currentUser?.fullName || 'N/A';
  const assignedTo = unitData?.rent_listing_agent?.name || null;
  const price = unitData?.rent_price_fees || null;
  const unitId = unitData?.id || null;
  const isUnitAvailable =  unitData?.unitStatus?.value === "available"   

  const defaultRemarks = useCallback(() => {
    return `Dear ${assignedTo || 'Agent'},
The agent ${createdBy} has requested you to send an offer letter to the following lead: ${selectedLead ? `${selectedLead.leadId} ${selectedLead.leadName}` : 'N/A'}, with the proposed rental price of ${price || 'N/A'}.`;
  }, [assignedTo, createdBy, selectedLead, price]);

  useEffect(() => {
    if (isOpen) {
      setRemarks(defaultRemarks());
    }
  }, [isOpen, defaultRemarks]);

  const fetchReferredLeads = useCallback(
    async (searchValue = '') => {
      setIsLoading(true);
      try {
        const response = await GetOpenTenantLeads({
          agentId: currentUser.userId,
          search: searchValue,
          pageIndex: 1,
          pageSize: 25,
        });
        setLeadData((response && response.result) || []);
      } catch (error) {
        console.error('Error fetching referred leads:', error);
        setLeadData([]);
        showError(t(`${translationPath}failed-to-fetch-leads`, 'Failed to fetch leads'));
      }
      setIsLoading(false);
    },
    [currentUser.userId, t, translationPath]
  );

  const debouncedFetchLeads = useCallback(
    debounce((value) => fetchReferredLeads(value), 300),
    [fetchReferredLeads]
  );

  const validateForm = useCallback(() => {
    if (!isUnitAvailable) {
      return t(`${translationPath}unit-not-available`, 'Unit is not available');
    }
    if (!assignedTo) {
      return t(`${translationPath}no-listing-agent-assigned`, 'No listing agent assigned to this unit');
    }
    if (!selectedLead) {
      return t(`${translationPath}please-select-lead-id`, 'Please select a Lead ID');
    }
    if (!price) {
      return t(`${translationPath}rental-price-missing`, 'Rental price is missing for the selected unit');
    }
    return null;
  }, [isUnitAvailable, assignedTo, selectedLead, price, t, translationPath]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedLead(null);
      setRemarks('');
      setSearchInput('');
      setLeadData([]);
    } else {
      fetchReferredLeads();
    }
  }, [isOpen, fetchReferredLeads]);

  const handleLeadChange = (event, newValue) => {
    setSelectedLead(newValue);
  };

  const handleRemarksChange = (event) => {
    setRemarks(event.target.value); 
  };

  const handleSearchInputChange = (event, newInputValue) => {
    setSearchInput(newInputValue);
    debouncedFetchLeads(newInputValue);
  };

  const handleSave = async (event) => {
    event.preventDefault();

    const errorMessage = validateForm();
    if (errorMessage) {
      showError(errorMessage);
      return;
    }

    setIsSaving(true);

    const activityData = {
      assignAgentId: unitData?.rent_listing_agent?.id || null,
      activityTypeId:  config.RequestSendingOfferLetter,
      unitId: unitData?.id || null,
      relatedUnitNumberId: unitData?.id || null,
      relatedLeadNumberId: selectedLead?.leadId || null,
      relatedContactNumberId: null,
      relatedPortfolioId: null,
      relatedWorkOrderId: null,
      relatedMaintenanceContractId: null,
      activityDate: new Date().toISOString(),
      activityEndDate: new Date().toISOString(),
      activityDuration: null,
      subject: activityName,
      comments: remarks, 
      isOpen: true,
      price: unitData?.rent_price_fees || null,
      activityReminders: [],
      fileId: null,
      fileName: null,
      relatedActivityToId: null,
      files: [],
    };

    try {
      const res = await CreateActivity(activityData);
      if (!(res && res.status && res.status !== 200)) {
        showSuccess(t(`${translationPath}activity-created-successfully`, 'Activity created successfully'));
        onClose();
      } else {
        showError(t(`${translationPath}activity-creation-failed`, 'Failed to create activity'));
      }
    } catch (error) {
      console.error('Error creating activity:', error);
      showError(t(`${translationPath}activity-creation-failed`, 'Failed to create activity'));
    } finally {
      setIsSaving(false);
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <DialogComponent
      isOpen={isOpen}
      onCloseClicked={handleClose}
      onSaveClicked={handleSave}
      titleText={t(`${translationPath}request-offer-letter`, 'Request Offer Letter')}
      maxWidth="sm"
      dialogContent={
        <div className="dialog-content">
          {!isUnitAvailable && (
            <div className="error-message flex">
              <span
                className="blink"
                style={{
                  display: 'block',
                  width: '15px',
                  height: '15px',
                  marginRight: '10px',
                  marginBottom: '10px',
                  marginLeft: '10px',
                  backgroundColor: '#06ff53',
                  opacity: 0.7,
                  borderRadius: '50%',
                  animation: 'blink 2.2s linear infinite',
                }}
              ></span>
              {t(`${translationPath}unit-not-available`, 'This action is only available for available units')}
            </div>
          )}
          <div className="mb-3">
            <Inputs
              idRef="activityNameRef"
              labelValue={t(`${translationPath}activity-name`, 'Activity Name')}
              value={activityName}
              onInputChanged={(event) => setActivityName(event.target.value || '')} 
            />
          </div>
          <div className="mb-3">
            <Inputs
              idRef="createdByRef"
              labelValue={t(`${translationPath}created-by`, 'Created By')}
              value={createdBy}
              isDisabled
            />
          </div>
          <div className="mb-3 flex w-100">
            <Inputs
              idRef="assignedToRef"
              labelValue={t(`${translationPath}assigned-to`, 'Assigned To')}
              value={assignedTo || 'N/A'}
              isDisabled
            />
            <Inputs
              idRef="priceRef"
              labelValue={t(`${translationPath}price`, 'Price')}
              value={price || 'N/A'}
              isDisabled
            />
            <Inputs
              idRef="unitIdRef"
              labelValue={t(`${translationPath}unit-id`, 'Unit ID')}
              value={unitId || 'N/A'}
              isDisabled
            />
          </div>
          <div className="mb-3">
            <AutocompleteComponent
              idRef="leadIdRef"
              labelValue={t(`${translationPath}lead-id`, 'Lead ID')}
              data={leadData}
              displayLabel={(option) => `( ${option.leadId} ) - ${option.leadName}`}
              chipsLabel={(option) => `( ${option.leadId} ) - ${option.leadName}`}
              multiple={false}
              selectedValues={selectedLead}
              onChange={handleLeadChange}
              onInputChange={handleSearchInputChange}
              inputPlaceholder={t(`${translationPath}search-leads`, 'Search Leads')}
              isLoading={isLoading}
              parentTranslationPath={parentTranslationPath}
              translationPath={translationPath}
            />
          </div>
          <div className="mb-3">
            <Inputs
              idRef="remarksRef"
              labelValue={t(`${translationPath}remarks`, 'Remarks')}
              value={remarks}
              onInputChanged={handleRemarksChange}
              multiline
              rows={4}
              inputPlaceholder={t(`${translationPath}edit-remarks`, 'Edit remarks')}
            />
          </div>
        </div>
      }
      parentTranslationPath={parentTranslationPath}
      translationPath={translationPath}
      saveClasses="btns theme-solid bg-primary w-100 mx-2 mb-2"
      onCancelClicked={handleClose}
      saveIsDisabled={isSaving}
    />
  );
};

export default RequestOfferLetterDialog;